import { useState, useRef, useEffect, useCallback } from 'react';
import { useIsMobile } from './use-mobile';

export const useBulletinCard = (
  isSelected: boolean, 
  onClose?: (id?: string) => void, 
  bulletinId?: string
) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDoubleClickHint, setShowDoubleClickHint] = useState(false);
  
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLikingRef = useRef(false);
  const isMobile = useIsMobile();
  const lastTapRef = useRef<number>(0);
  const lastTapPositionRef = useRef({ x: 0, y: 0 });
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartRef = useRef<{ x: number, y: number } | null>(null);
  const hasMoveRef = useRef(false);
  
  // Store the onDoubleTap callback
  const onDoubleTapRef = useRef<((e: React.MouseEvent) => void) | null>(null);
  
  // Clear all timeouts on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let hintTimeout: NodeJS.Timeout;
    if (isSelected && !isMobile) {
      hintTimeout = setTimeout(() => {
        setShowDoubleClickHint(true);
        setTimeout(() => setShowDoubleClickHint(false), 3000);
      }, 3000);
    } else {
      setShowDoubleClickHint(false);
    }
    return () => clearTimeout(hintTimeout);
  }, [isSelected, isMobile]);
  
  // Reset image loaded state when selected state changes
  useEffect(() => {
    if (isSelected) {
      console.log(`[BulletinCard ${bulletinId}] Selected, resetting image loaded state`);
    }
  }, [isSelected, bulletinId]);

  // Function to set the onDoubleTap callback
  const setLongPressCallback = useCallback((callback: (e: React.MouseEvent) => void) => {
    onDoubleTapRef.current = callback;
  }, []);

  // Check if click is in the like corner (bottom-right 48px area)
  const isClickInLikeCorner = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const target = e.currentTarget as HTMLElement;
    if (!target) return false;
    
    const rect = target.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Check if click is in bottom-right 48px corner (larger for mobile)
    const cornerSize = 48;
    return (x >= rect.width - cornerSize && y >= rect.height - cornerSize);
  }, []);

  // Handle corner like clicks
  const handleCornerLike = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!onDoubleTapRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    isLikingRef.current = true;
    console.log(`[BulletinCard ${bulletinId}] Corner like triggered`);
    
    // Create a synthetic mouse event for the onDoubleTap callback
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
      currentTarget: e.currentTarget,
      target: e.target
    } as unknown as React.MouseEvent;
    
    onDoubleTapRef.current(syntheticEvent);
    
    setTimeout(() => {
      isLikingRef.current = false;
    }, 500);
  }, [bulletinId]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Check if click is in like corner first
    if (isClickInLikeCorner(e)) {
      handleCornerLike(e);
      return;
    }
    
    e.preventDefault();
    
    if (isLikingRef.current) {
      console.log('Click ignored - bulletin is being liked');
      return;
    }

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    const timeoutDuration = isMobile ? 100 : 150;
    
    clickTimeoutRef.current = setTimeout(() => {
      if (!isLikingRef.current) {
        console.log(`[BulletinCard ${bulletinId}] Opening bulletin inspection view`);
        if (isSelected) {
          onClose?.();
        } else {
          onClose?.(bulletinId);
        }
      }
      clickTimeoutRef.current = null;
    }, timeoutDuration);
  }, [isSelected, onClose, bulletinId, isMobile, isClickInLikeCorner, handleCornerLike]);

  const isSimilarPosition = (x1: number, y1: number, x2: number, y2: number) => {
    const threshold = 30;
    return Math.abs(x1 - x2) < threshold && Math.abs(y1 - y2) < threshold;
  };

  // Improved touch start handler with tracking of initial position
  const handleTouchStart = useCallback((e: React.TouchEvent, onDoubleTap: (e: React.MouseEvent) => void) => {
    // Check if touch is in like corner first
    if (isClickInLikeCorner(e)) {
      console.log('[useBulletinCard] Touch in like corner detected, handling corner like');
      handleCornerLike(e);
      return;
    }
    
    // Clear any existing timeouts
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    
    const touch = e.touches[0];
    const now = Date.now();
    const timeDiff = now - lastTapRef.current;
    
    const touchX = touch.clientX;
    const touchY = touch.clientY;
    
    // Store the initial touch position for move detection
    touchStartRef.current = { x: touchX, y: touchY };
    hasMoveRef.current = false;
    
    console.log('[useBulletinCard] Touch start:', {
      bulletinId,
      timeDiff,
      position: { x: touchX, y: touchY },
      lastPosition: lastTapPositionRef.current,
    });
    
    // Keep double tap detection for desktop
    if (!isMobile && timeDiff < 300 && 
        isSimilarPosition(
          touchX, 
          touchY, 
          lastTapPositionRef.current.x, 
          lastTapPositionRef.current.y
        )) {
      console.log('[useBulletinCard] Double tap detected, triggering like');
      isLikingRef.current = true;
      onDoubleTap(e as unknown as React.MouseEvent);
      
      // Reset liking state after a delay
      setTimeout(() => {
        isLikingRef.current = false;
      }, 500);
      
      e.preventDefault();
      e.stopPropagation();
    }
    
    lastTapPositionRef.current = { x: touchX, y: touchY };
    lastTapRef.current = now;
  }, [bulletinId, isMobile, isClickInLikeCorner, handleCornerLike]);

  // New touch move handler to detect scrolling intent
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    
    const touch = e.touches[0];
    const moveX = Math.abs(touch.clientX - touchStartRef.current.x);
    const moveY = Math.abs(touch.clientY - touchStartRef.current.y);
    
    // If movement is greater than threshold, consider it a scroll
    const moveThreshold = 10; // pixels
    if (moveX > moveThreshold || moveY > moveThreshold) {
      hasMoveRef.current = true;
      
      // Clear any opening timeout to prevent accidental opening
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }
      
      console.log('[useBulletinCard] Touch move detected, preventing click');
    }
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // Don't handle if there was significant movement (scrolling)
    if (hasMoveRef.current) {
      console.log('[useBulletinCard] Touch end ignored due to movement/scrolling');
      touchStartRef.current = null;
      return;
    }
    
    // Clear any existing timeout to prevent duplicate actions
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    
    // Only handle touch end if not currently liking
    if (!isLikingRef.current) {
      // Use shorter delay on mobile to make UI feel more responsive
      const delay = isMobile ? 100 : 200;
      
      touchTimeoutRef.current = setTimeout(() => {
        console.log('[useBulletinCard] Touch end - opening inspection view');
        if (!isSelected) {
          onClose?.(bulletinId);
        } else {
          onClose?.();
        }
      }, delay);
    }
    
    touchStartRef.current = null;
  }, [bulletinId, isSelected, onClose, isMobile]);

  const handleDoubleClick = useCallback((e: React.MouseEvent, onDoubleTap: (e: React.MouseEvent) => void) => {
    if (isMobile) return;
    
    console.log('[useBulletinCard] handleDoubleClick fired', {
      event: e,
      bulletinId,
      isSelected
    });
    
    e.preventDefault();
    e.stopPropagation();

    isLikingRef.current = true;
    
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    onDoubleTap(e);
    
    setTimeout(() => {
      isLikingRef.current = false;
    }, 500);
  }, [bulletinId, isSelected, isMobile]);

  return {
    isHovered,
    setIsHovered,
    imageLoaded,
    setImageLoaded,
    isDeleting,
    setIsDeleting,
    showDoubleClickHint,
    handleClick,
    handleDoubleClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    setLongPressCallback,
    isClickInLikeCorner,
    handleCornerLike,
    isMobile
  };
};
